'use client';

import {
	DataGridPremium,
	GridColDef,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	useGridApiRef,
} from '@mui/x-data-grid-premium';
import { useMutation, useQuery } from '@parkchirp/api-client';
import { cloneDeep } from 'lodash';
import { ResourceTypes } from 'parkchirp-shared/enums';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CommonContext } from '../../../contexts';
import { useDebounce } from '../../../hooks/useDebounce';
import { DELETE_COUNT_AND_RATE } from '../../../mutations';
import { GET_LEASING_RIGHTS, GET_LEASING_RIGHTS_CSV } from '../../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { convertMuiFilterForV2Api, datagridFilterPanelAttributes } from '../../../utils';
import { AuditHistoryModal } from '../../audit';
import { DatagridLinearProgress } from '../../loading';
import { CsvExportButtonV2 } from '../../reports';
import { CustomToolbar } from '../../toolbar';
import { AddLeasingRightButton } from './addLeasingRightButton';
import { getLeasingRightsColumns } from './leasingRightsColumns';

type LeasingRightsTableProps = {
	subscriptionId?: string;
	accountId?: string | number;
	initialData?: any[];
	initialCount?: number;
	readOnly?: boolean;
	setShowDialog?: SetShowDialog;
	setShowSnackbar?: SetShowSnackbar;
	getUrlToSubscriptionPage?: (...x: any[]) => string;
	compact?: boolean;
};

export const LeasingRightsTable = ({
	subscriptionId,
	accountId,
	setShowDialog,
	setShowSnackbar,
	getUrlToSubscriptionPage,
	initialData = [],
	initialCount = 0,
	readOnly = false,
	compact = false,
}: LeasingRightsTableProps) => {
	const apiRef = useGridApiRef();
	const [rows, setRows] = useState<any[] | null>(initialData);
	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [rowCount, setRowCount] = useState(initialCount);
	const [defaultSortOrder] = useState({ field: 'createdAt', sort: 'desc' });
	const [sortOrder, setSortOrder] = useState(defaultSortOrder);
	const [filters, setFilters] = useState([]);
	const debouncedSetFilters = useDebounce(setFilters, 500);
	const [loading, setLoading] = useState<boolean>(!initialData.length);

	const { hasComponentAccess } = useContext(CommonContext);

	const [deleteCountAndRate, { loading: deleteLoading }] = useMutation(DELETE_COUNT_AND_RATE);

	const { data } = useQuery(GET_LEASING_RIGHTS, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		variables: {
			limit: pageSize,
			offset: page * pageSize,
			filters,
			order: sortOrder,
			subscriptionId,
			includeParentLeases: !readOnly,
		},
		skip: window.location.href.includes('subscription-info') && !subscriptionId,
		notifyOnNetworkStatusChange: true,
		ssr: false,
	});

	useEffect(() => {
		if (data?.getLeasingRights) {
			setRows(
				cloneDeep(
					data.getLeasingRights.leasingRights.map((lr) => ({
						...lr,
						childSubtenantAccountIds: lr.childSubtenantAccountIds.join(', '),
						detachedSubtenantAccountIds: lr.detachedSubtenantAccountIds.join(', '),
					})),
				),
			);
			setRowCount(data.getLeasingRights.count);
			setLoading(false);
		}
	}, [data]);

	const onSortModelChange = useCallback(
		(sortModel: GridSortModel) => {
			setLoading(true);
			setPaginationModel((prev) => ({ ...prev, page: 0 }));
			setSortOrder(sortModel[0] ?? defaultSortOrder);
		},
		[defaultSortOrder],
	);

	const onFilterModelChange = useCallback(
		(filterModel: GridFilterModel) => {
			setLoading(true);
			setPaginationModel((prev) => ({ ...prev, page: 0 }));

			const { items: filterItems } = filterModel;

			if (!filterItems.length) {
				debouncedSetFilters([]);
				return;
			}

			const queryFilters = filterItems.flatMap((filter) => {
				const { field, operator, value } = filter;

				if (value == null && !['isEmpty', 'isNotEmpty'].includes(operator)) return [];

				return {
					propertyName: field,
					op: convertMuiFilterForV2Api(operator, value).op,
					value,
				};
			});

			debouncedSetFilters(queryFilters);
		},
		[debouncedSetFilters],
	);

	const handleAuditHistory = async (leasingRightId: string, timezone: string) => {
		const auditDialog = (
			<AuditHistoryModal
				title='Leasing Right Audit History'
				resourceId={leasingRightId}
				resourceType={ResourceTypes.LEASE}
				timezone={timezone}
				onClose={() => {
					setShowDialog(null);
				}}
			/>
		);

		setShowDialog(auditDialog);
	};

	const columns: GridColDef[] = getLeasingRightsColumns({
		setShowDialog,
		rows,
		setRows,
		deleteLeasingCountAndRate: deleteCountAndRate,
		setShowSnackbar,
		getUrlToSubscriptionPage,
		readOnly,
		hasComponentAccess,
		handleAuditHistory,
	});

	return (
		<div className='pca__datagrid-sticky--headfoot'>
			<DataGridPremium
				sx={{ height: compact ? '42vh' : '100%' }}
				disableRowSelectionOnClick
				apiRef={apiRef}
				rows={!loading && rows ? rows : []}
				columns={columns}
				columnVisibilityModel={{ actions: !readOnly }}
				loading={loading || deleteLoading || !rows}
				paginationModel={{ page, pageSize }}
				pageSizeOptions={[pageSize]}
				rowCount={rowCount}
				onSortModelChange={onSortModelChange}
				onFilterModelChange={onFilterModelChange}
				onPaginationModelChange={(newModel) => {
					setLoading(true);
					setPaginationModel(newModel);
				}}
				pagination
				filterMode='server'
				paginationMode='server'
				sortingMode='server'
				slots={{
					toolbar: () => (
						<CustomToolbar
							{...(!readOnly && {
								customAction: <AddLeasingRightButton disabled={loading} accountId={accountId} />,
								otherItems: [
									<CsvExportButtonV2
										query={GET_LEASING_RIGHTS_CSV}
										queryName='getLeasingRightsCsv'
										args={{
											variables: {
												filters,
												order: sortOrder,
												subscriptionId,
												includeParentLeases: !readOnly,
											},
											context: {
												apiVersion: 'v2',
											},
										}}
									/>,
								],
							})}
						/>
					),
					loadingOverlay: DatagridLinearProgress,
				}}
				slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
				data-cy='contract-rules-table-container'
			/>
		</div>
	);
};
